import { Box, Typography } from '@mui/material'
import { theme } from 'config'
import React from 'react'
import styled, { css } from 'styled-components'
import { debounce } from 'util/util'

export interface ITextFieldProps {
  autoFocus?: boolean
  className?: string
  fontWeight?: string | number
  fontSize?: string | number
  underline?: boolean
  editOnFocus?: boolean
  debounced?: boolean
  placeholder?: string
  value?: string
  prefix?: string
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  onFocus?: (value: string) => void
  defaultValue?: string | number | ReadonlyArray<string> | undefined
  blurOnEnter?: boolean
  onClick?: () => void
}

export const EditorInput: React.FC<ITextFieldProps> = ({
  blurOnEnter,
  onBlur,
  onFocus,
  defaultValue,
  className,
  fontWeight,
  fontSize,
  editOnFocus,
  value,
  prefix = '',
  onChange,
  underline,
  placeholder,
}) => {
  const [focused, setFocused] = React.useState(false)
  const debouncedChange = onChange
    ? React.useCallback(debounce(onChange), [onChange])
    : () => {}

  return (
    <Box>
      {!focused && editOnFocus ? (
        <Box
          width='100%'
          sx={{ overflow: 'hidden' }}
          onDoubleClick={() => setFocused(true)}
        >
          <Typography fontSize={fontSize} fontWeight={fontWeight}>
            {prefix && prefix !== undefined
              ? `${prefix}. ${defaultValue}`
              : defaultValue}
          </Typography>
        </Box>
      ) : (
        <Input
          defaultValue={defaultValue}
          className={className}
          autoFocus={editOnFocus}
          placeholder={placeholder}
          value={value}
          type='text'
          onKeyDown={
            blurOnEnter
              ? (e) => {
                  if (e.key === 'Enter') e.currentTarget.blur()
                }
              : undefined
          }
          onChange={(e) => debouncedChange(e.target.value)}
          fontWeight={fontWeight}
          underline={underline}
          onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => {
            setFocused(false)
            onBlur && onBlur(e.target.value as string)
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement, Element>) => {
            onFocus && onFocus(e.target.value as string)
          }}
        />
      )}
    </Box>
  )
}

const Input = styled.input<{ fontWeight: string | number; underline: boolean }>`
  background-color: transparent;
  border: none;
  width: 100%;
  color: currentColor;
  font-size: 1.6rem;
  padding: 0;
  outline: none;
  font-weight: ${(p) => p.fontWeight || `${theme.typography.fontWeightBold}`};

  ${(p) =>
    p.underline &&
    css`
      padding: 1.7rem 1.4rem;
      border-bottom: 1px solid ${theme.palette.grey[400]};
    `}
`
